import { syncIdogcamCameras, updateIdogcamIntegration } from '../api/IdogcamApi';
import { IdogcamCredentials, IdogcamIntegration } from '../model/IdogcamModel';

// The Cuddles platform key for iDogCam is missing on the server: nothing the business can fix.
export class IdogcamUnavailableError extends Error {
    constructor() {
        super('iDogCam is not available');
        this.name = 'IdogcamUnavailableError';
    }
}

// iDogCam rejected the Kennel ID / ERP Code (or could not be reached with them).
export class IdogcamInvalidCredentialsError extends Error {
    constructor() {
        super('iDogCam rejected the Kennel ID or ERP Code');
        this.name = 'IdogcamInvalidCredentialsError';
    }
}

const EMPTY_CREDENTIALS: IdogcamCredentials = { kennelId: '', erpcode: '' };

interface ConnectIdogcamParams {
    marketplaceId: number;
    token: string;
    credentials: IdogcamCredentials;
    // Credentials to restore if the new ones fail; null when connecting for the first time.
    previousCredentials: IdogcamCredentials | null;
}

const restoreCredentials = async (
    marketplaceId: number,
    token: string,
    previousCredentials: IdogcamCredentials | null
) => {
    try {
        await updateIdogcamIntegration(marketplaceId, token, previousCredentials ?? EMPTY_CREDENTIALS);
    } catch {
        // Best effort: the original error is the one worth reporting to the user.
    }
};

// The backend stores Kennel ID / ERP Code without checking them against iDogCam, so the
// connection is validated with a sync right after saving. If it fails, the previous values
// are restored so a location never stays "connected" with credentials that don't work.
export const connectIdogcam = async ({
    marketplaceId,
    token,
    credentials,
    previousCredentials
}: ConnectIdogcamParams): Promise<IdogcamIntegration> => {
    const integration = await updateIdogcamIntegration(marketplaceId, token, credentials);

    if (!integration.platformConfigured) {
        await restoreCredentials(marketplaceId, token, previousCredentials);
        throw new IdogcamUnavailableError();
    }

    try {
        await syncIdogcamCameras(marketplaceId, token);
    } catch {
        await restoreCredentials(marketplaceId, token, previousCredentials);
        throw new IdogcamInvalidCredentialsError();
    }

    return integration;
};
